import PermissionsEnum from 'const/permissions';
import RolesEnum from 'const/role';
import AppRoutes from 'const/routes';
import SessionStatus from 'const/session';
import { UserI, UserRol } from 'interfaces';
import { Session } from 'next-auth';
import { SessionContextValue } from 'next-auth/react';

// --- --- Constants to redirect the user

// If the user is not logged already, it will be redirected to this path
const PATH_TO_REDIRECT_NOT_LOGGED_CLIENT = AppRoutes.CLIENTAPP_AUTH_SIGN_IN;
const PATH_TO_REDIRECT_NOT_LOGGED_LAWYER = AppRoutes.LAWYERAPP_AUTH_SIGN_IN;

// If the user is logged already, it will be redirected to this path
const PATH_TO_REDIRECT_LOGGED_CLIENT = AppRoutes.CLIENTAPP_DASHBOARD;
const PATH_TO_REDIRECT_LOGGED_LAWYER = AppRoutes.LAWYERAPP_DASHBOARD;

// If the user is not authorized, it will be redirected to this path
const PATH_TO_NOT_AUTHORIZED = AppRoutes.NOT_AUTHORIZED;

// If the user have enabled 2FA
const PATH_TO_TWO_FACTOR = AppRoutes.CLIENTAPP_AUTH_TWO_FACTOR;

// Send the user to this page to force a logout
const PATH_TO_LOGOUT_CLIENT = AppRoutes.CLIENTAPP_AUTH_LOGOUT;
const PATH_TO_LOGOUT_LAWYER = AppRoutes.LAWYERAPP_AUTH_LOGOUT;

// Paths that will be public, not log in necessary
const PUBLIC_PATHS = [
	AppRoutes.CLIENTAPP_AUTH_FORGOT_PASSWORD,
	AppRoutes.CLIENTAPP_AUTH_NEW_PASSWORD,
	AppRoutes.CLIENTAPP_AUTH_SIGN_IN,
	AppRoutes.CLIENTAPP_AUTH_SIGN_UP,
	AppRoutes.CLIENTAPP_AUTH_TWO_FACTOR,
	AppRoutes.NOT_AUTHORIZED,
	AppRoutes.NOT_FOUND,
];

// Errors that can occur related to login or session
export enum LoginError {
	NOT_AUTHORIZED,
	TOKEN_EXPIRED,
}

/**
 * Utility class for authentication-related operations
 *
 * This class should be used client-side
 */
class AuthUtils {
	//  Session instance
	private session: SessionContextValue | null = null;

	constructor(
		private publicPaths: Set<string> = new Set<string>(),
		private pathToRedirectNotLoggedClient: string = PATH_TO_REDIRECT_NOT_LOGGED_CLIENT,
		private pathToRedirectNotLoggedLawyer: string = PATH_TO_REDIRECT_NOT_LOGGED_LAWYER,
		private pathToRedirectTwoFactor: string = PATH_TO_TWO_FACTOR,
		private pathToRedirectLoggerClient: string = PATH_TO_REDIRECT_LOGGED_CLIENT,
		private pathToRedirectLoggerLawyer: string = PATH_TO_REDIRECT_LOGGED_LAWYER,
		private pathToNotAuthorized: string = PATH_TO_NOT_AUTHORIZED,
		private pathToLogoutClient: string = PATH_TO_LOGOUT_CLIENT,
		private pathToLogoutLawyer: string = PATH_TO_LOGOUT_LAWYER
	) {
		PUBLIC_PATHS.forEach((path) => !!path && this.publicPaths.add(path));
	}

	// -- Session -- //
	// Before using any of the utils functions session should be SET
	// The layout component will be in charge of setting this value, but devs should keep track if this fails for some reason
	public setSession(session: SessionContextValue | null): void {
		this.session = session;
	}

	public getSession(): SessionContextValue | null {
		return this.session;
	}

	// In case session is not set then an error message should be throw
	protected checkSession(): boolean {
		if (this.session == null) {
			throw new Error(
				'Session is null. Please set the session before calling this method.'
			);
		}

		return true;
	}

	protected isAuthenticatedSession(): boolean {
		return this.session?.status == SessionStatus.AUTHENTICATED;
	}

	// -- Getters -- //
	public getPublicPaths(): Set<string> {
		return this.publicPaths;
	}
	public getPathToRedirectNotLoggedClient(): string {
		return this.pathToRedirectNotLoggedClient;
	}
	public getPathToRedirectNotLoggedLawyer(): string {
		return this.pathToRedirectNotLoggedLawyer;
	}
	public getPathToRedirectNotLoggeUser(role: UserRol): string {
		if (role === 'client') {
			return this.pathToRedirectNotLoggedClient;
		} else {
			return this.pathToRedirectNotLoggedLawyer;
		}
	}
	public getPathToRedirectNotCompleted2FA(): string {
		return this.pathToRedirectTwoFactor;
	}
	public getPathToNotAuthorized(): string {
		return this.pathToNotAuthorized;
	}
	public getPathToRedirectLoggedClient(): string {
		return this.pathToRedirectLoggerClient;
	}
	public getPathToRedirectLoggedLawyer(): string {
		return this.pathToRedirectLoggerLawyer;
	}
	public getPathToRedirectLoggedUser(role: UserRol): string {
		if (role === 'client') {
			return this.pathToRedirectLoggerClient;
		} else {
			return this.pathToRedirectLoggerLawyer;
		}
	}
	public getPathToLogout(role: UserRol): string {
		if (role === 'client') {
			return this.pathToLogoutClient;
		} else {
			return this.pathToLogoutLawyer;
		}
	}

	// --- --- Util functions --- --- //

	// Get session user, devs might follow manual useSession approach without any problem
	public getUser(): UserI | null {
		this.checkSession();
		if (this.isAuthenticatedSession() && this.session && this.session.data)
			return this.session.data.user;
		return null;
	}

	// Get the user Roles
	public getUserRole(): UserRol | null {
		const user = this.getUser();
		if (user) return user.role;
		return null;
	}

	// Get the user permissions
	public getUserPermissions(): PermissionsEnum[] | string[] {
		const user = this.getUser();
		// if (user && user.permissions) return user.permissions;
		return [];
	}

	// Check if user has an specific role
	public hasRole(roleName: string): boolean {
		const user: UserI | null = this.getUser();
		if (user) user.role === roleName;
		return false;
	}

	// Check if user has a set of roles
	public hasAnyRole(roleNames: string[]): boolean {
		const user: UserI | null = this.getUser();
		// if (user) return user.roles?.some((role) => roleNames.includes(role));
		return false;
	}

	// Check if user has a specific permission
	public hasPermissionFor(permission: string): boolean {
		const user: UserI | null = this.getUser();

		// if (user) return user.permissions?.some((p) => p == permission);
		return false;
	}

	// Check if user has any of the permission provided
	public hasAnyPermission(permissions: string[]): boolean {
		const user: UserI | null = this.getUser();
		if (user) {
			// return user.permissions?.some((p) => permissions.includes(p));
		}

		return false;
	}

	// If the user is allowed to do a certain action based on his permissions
	// then this method allows it or decline it
	public ifAllowed<T>(
		permissionUUIDs: string[],
		ifTrue: T,
		ifFalse?: T
	): T | undefined {
		if (this.hasAnyPermission(permissionUUIDs)) return ifTrue;
		return ifFalse;
	}

	// Check if the user session is still on
	public isLogged(): boolean {
		this.checkSession();

		if (this.session && this.session.data) {
			const expirationDate = new Date(this.session?.data?.expires);
			const currentDate = new Date();
			return currentDate < expirationDate;
		}
		return false;
	}

	// Redirects the user based on its state on the app
	public redirectUserIfNeeded(): void {
		// Here we dont want to throw an error if the user is not logged
		// or if the session is null, we want to redirect instead so
		// this is why we dont re-use the above functions
		if (typeof window === 'undefined') {
			console.log('<- [Auth utils] window is undefined');
			return;
		}

		const isSessionActive =
			this.session != null && this.session != undefined ? true : false;
		let isSessionExpired = false;

		if (this.session && this.session.data) {
			const expirationDate = new Date(this.session.data.expires);
			const currentDate = new Date();
			isSessionExpired = currentDate > expirationDate;
		}

		// If there is no session: the user is NOT LOGGED IN
		// but if the user is trying to access a page that is not public then redirect
		if (!isSessionActive && !this.publicPaths.has(window.location.pathname)) {
			const user: UserI | null = this.session?.data?.user;
			if (user?.role === 'client') {
				window.location.href = this.pathToRedirectNotLoggedClient;
			} else {
				window.location.href = this.pathToRedirectNotLoggedLawyer;
			}
			// window.location.href = this.pathToRedirectNotLoggedUser;
			return;
		}

		// If there is a session and the token has expired: the user is NOT LOGGED IN
		// and is not on a public page
		if (
			isSessionActive &&
			isSessionExpired &&
			!this.publicPaths.has(window.location.pathname)
		) {
			// window.location.href = this.pathToRedirectNotLoggedUser;
			const user: UserI | null = this.session?.data?.user;
			if (user?.role === 'client') {
				window.location.href = this.pathToRedirectNotLoggedClient;
			} else {
				window.location.href = this.pathToRedirectNotLoggedLawyer;
			}
			return;
		}

		// If there is a session and the token is not expired the user is LOGGED IN
		// but the user is looking the auth pages, redirect him (or her)
		const authPaths = new Set<string>([
			AppRoutes.CLIENTAPP_AUTH_FORGOT_PASSWORD,
			AppRoutes.CLIENTAPP_AUTH_SIGN_IN,
			AppRoutes.CLIENTAPP_AUTH_SIGN_UP,
		]);
		if (
			isSessionActive &&
			!isSessionExpired &&
			authPaths.has(window.location.pathname)
		) {
			// window.location.href = this.pathToRedirectLoggedUser;
			const user: UserI | null = this.session?.data?.user;
			if (user?.role === 'client') {
				window.location.href = this.pathToRedirectNotLoggedClient;
			} else {
				window.location.href = this.pathToRedirectNotLoggedLawyer;
			}
			return;
		}

		// If there is no session then redirect
		// this is last resort, probably the other if are going to be called
		if (!isSessionActive) {
			// window.location.href = this.pathToRedirectNotLoggedUser;
			const user: UserI | null = this.session?.data?.user;
			if (user?.role === 'client') {
				window.location.href = this.pathToRedirectNotLoggedClient;
			} else {
				window.location.href = this.pathToRedirectNotLoggedLawyer;
			}
			return;
		}
	}
}

const authUtils = new AuthUtils();
export default authUtils;
